import { useEffect, useRef, useState } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';

import { Check, ExternalLink, Info, MapPin } from 'lucide-react';

import { Button } from 'components/Button';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import {
  LTLShiplifyLookupMetadata,
  LTLShiplifyLookupStatus,
} from 'lib/api/getLTLQuickQuotes';
import {
  ShiplifyLocationLookupResponse,
  getShiplifyLocationLookup,
} from 'lib/api/getShiplifyLocationLookup';
import { QuickQuoteInputs } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { StopTypes } from 'types/Appointment';
import { Maybe, Undef } from 'types/UtilityTypes';
import { AccessorialIntegration } from 'types/enums/Integrations';
import { cn } from 'utils/shadcn';

import {
  formatShiplifyLocationType,
  getShiplifyAddressKey,
  getShiplifyStop,
  isShiplifyDestinationComplete,
} from '../shiplifyLookup';

type ShiplifyLookupSectionProps = {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  stopIndex: number;
  stopType: StopTypes;
};

const SHIPLIFY_LOOKUP_WARNINGS: Partial<
  Record<LTLShiplifyLookupStatus, string>
> = {
  [LTLShiplifyLookupStatus.ConnectionError]:
    'Shiplify needs to be reconnected. You can still request rates.',
  [LTLShiplifyLookupStatus.FailedOpen]:
    "Shiplify couldn't be checked right now. You can still request rates.",
  [LTLShiplifyLookupStatus.NotFound]:
    "This address wasn't found in Shiplify. Check the address or continue requesting rates.",
  [LTLShiplifyLookupStatus.RateLimited]:
    "Shiplify's daily lookup limit has been reached. You can still request rates.",
};

function metadataFromResponse(
  response: ShiplifyLocationLookupResponse
): LTLShiplifyLookupMetadata {
  return {
    status: response.status,
    shiplifyStatus: response.shiplifyStatus,
    recommendations: response.recommendations?.map((recommendation) => ({
      ...recommendation,
      codes: Array.isArray(recommendation.codes) ? recommendation.codes : [],
    })),
    locationTypes: response.locationTypes,
    dockAccess: response.dockAccess,
    forklift: response.forklift,
    locationId: response.locationId,
    externalUrl: response.externalUrl,
  };
}

export function ShiplifyLookupSection({
  formMethods,
  stopIndex,
  stopType,
}: ShiplifyLookupSectionProps) {
  const { accessorialIntegrations } = useServiceFeatures();
  const isShiplifyLocationLookupEnabled = accessorialIntegrations.some(
    (integration) => integration.name === AccessorialIntegration.Shiplify
  );
  const { control, getValues, setValue } = formMethods;
  const { toast } = useToast();
  const [isChecking, setIsChecking] = useState(false);
  const verifiedAddressKeyRef = useRef<Maybe<string>>(null);
  const lookupRequestIdRef = useRef(0);

  const stopPath = `stops.${stopIndex}` as 'stops.0' | 'stops.1';
  const watchedStop = useWatch({ control, name: stopPath });
  const lookupPath =
    stopType === StopTypes.Pickup
      ? 'ltl.shiplifyLookups.pickup'
      : 'ltl.shiplifyLookups.dropoff';
  const storedLookup = useWatch({
    control,
    name: lookupPath,
  }) as Undef<LTLShiplifyLookupMetadata>;
  const lookup = storedLookup;
  const destination = getShiplifyStop(watchedStop ? [watchedStop] : [], 0, {
    requireLocationLineFullAddress: true,
  });
  const isAddressComplete = isShiplifyDestinationComplete(destination);
  const addressKey = getShiplifyAddressKey(destination);

  const locationTypeLabel = (lookup?.locationTypes ?? [])
    .map(formatShiplifyLocationType)
    .filter(Boolean)
    .join(', ');
  const hasCompletedLookup =
    lookup?.status === LTLShiplifyLookupStatus.Recommendations ||
    lookup?.status === LTLShiplifyLookupStatus.NoRecommendations;

  useEffect(() => {
    // Associate server-provided lookup state with the address initially shown.
    if (!storedLookup || verifiedAddressKeyRef.current !== null) {
      return;
    }
    verifiedAddressKeyRef.current = addressKey;
  }, [addressKey, storedLookup]);

  useEffect(() => {
    // A changed address invalidates only that stop's lookup and selections.
    if (
      !lookup ||
      verifiedAddressKeyRef.current === null ||
      verifiedAddressKeyRef.current === addressKey
    ) {
      return;
    }

    const selected = getValues('ltl.selectedAccessorials') ?? [];
    setValue(
      'ltl.selectedAccessorials',
      selected.filter((item) => item.recommendedByShiplifyStop !== stopType),
      { shouldDirty: true }
    );
    const lookups = getValues('ltl.shiplifyLookups') ?? {};
    const { [stopType]: _removed, ...remainingLookups } = lookups;
    setValue('ltl.shiplifyLookups', remainingLookups, { shouldDirty: true });
    verifiedAddressKeyRef.current = null;
    lookupRequestIdRef.current += 1;
    toast({
      description: 'Destination changed. Check Shiplify again.',
      variant: 'warning',
    });
  }, [addressKey, getValues, lookup, setValue, stopType, toast]);

  if (!isShiplifyLocationLookupEnabled) {
    return null;
  }

  const showLookupWarning = (status: LTLShiplifyLookupStatus) => {
    const description = SHIPLIFY_LOOKUP_WARNINGS[status];
    if (description) {
      toast({ description, variant: 'warning' });
    }
  };

  const performLookup = async () => {
    if (!destination || !isAddressComplete) {
      return;
    }

    const requestId = ++lookupRequestIdRef.current;
    const requestedAddressKey = addressKey;

    const result = await getShiplifyLocationLookup(stopType, {
      ...destination,
      addressLine1: destination.addressLine1!,
      country: destination.country!,
    });

    const currentDestination = getShiplifyStop(getValues('stops'), stopIndex, {
      requireLocationLineFullAddress: true,
    });
    const isCurrentRequest =
      requestId === lookupRequestIdRef.current &&
      getShiplifyAddressKey(currentDestination) === requestedAddressKey;

    if (!isCurrentRequest) {
      return;
    }
    verifiedAddressKeyRef.current = requestedAddressKey;

    if (result.isErr()) {
      const failedLookup: LTLShiplifyLookupMetadata = {
        status: LTLShiplifyLookupStatus.FailedOpen,
      };
      setValue(
        'ltl.shiplifyLookups',
        {
          ...(getValues('ltl.shiplifyLookups') ?? {}),
          [stopType]: failedLookup,
        },
        { shouldDirty: true }
      );
      showLookupWarning(failedLookup.status);
      return;
    }

    const metadata = metadataFromResponse(result.value);
    setValue(
      'ltl.shiplifyLookups',
      { ...(getValues('ltl.shiplifyLookups') ?? {}), [stopType]: metadata },
      { shouldDirty: true }
    );
    showLookupWarning(metadata.status);
  };

  const handleLookup = async () => {
    setIsChecking(true);
    try {
      await performLookup();
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <Flex direction='col' gap='xs' className='w-full items-start'>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex' tabIndex={0}>
            <Button
              type='button'
              variant='secondary'
              size='auto'
              disabled={!isAddressComplete || isChecking}
              onClick={() => void handleLookup()}
              buttonNamePosthog={null}
              className={cn(
                'h-7 gap-1.5 px-2.5 py-1 text-xs',
                'disabled:cursor-not-allowed disabled:border-neutral-200 disabled:bg-neutral-50 disabled:text-neutral-400 disabled:opacity-100 disabled:[&>svg]:!stroke-neutral-400',
                hasCompletedLookup
                  ? 'border-success-300 bg-success-50 text-success-700 hover:border-success-400 hover:bg-success-50 hover:text-success-700 hover:[&>svg]:!stroke-success-700'
                  : 'border-brand-300 text-brand-main'
              )}
            >
              {isChecking && <ButtonLoader />}
              {hasCompletedLookup ? 'Imported from Shiplify' : 'Check Shiplify'}
              {hasCompletedLookup ? (
                <Check aria-hidden='true' className='h-3.5 w-3.5' />
              ) : (
                <Info aria-hidden='true' className='h-3.5 w-3.5' />
              )}
            </Button>
          </span>
        </TooltipTrigger>
        {!isAddressComplete && (
          <TooltipContent side='bottom' className='max-w-64 text-xs'>
            Add a full address to check Shiplify.
          </TooltipContent>
        )}
      </Tooltip>

      {!isAddressComplete && (
        <Typography
          as='span'
          variant='body-xxs'
          className='text-accent-main opacity-75'
        >
          Add a full address to check Shiplify.
        </Typography>
      )}

      {isAddressComplete && !hasCompletedLookup && !isChecking && (
        <Typography
          as='span'
          variant='body-xxs'
          className='text-accent-main opacity-75'
        >
          Check Shiplify for this address before requesting rates
        </Typography>
      )}

      {lookup?.status === LTLShiplifyLookupStatus.NoRecommendations && (
        <Typography as='span' variant='body-xs' textColor='lessMuted'>
          Checked — no accessorials recommended
        </Typography>
      )}

      {lookup &&
        (lookup.status === LTLShiplifyLookupStatus.Recommendations ||
          lookup.status === LTLShiplifyLookupStatus.NoRecommendations) &&
        (locationTypeLabel ||
          lookup.dockAccess != null ||
          lookup.forklift != null) && (
          <Flex
            direction='col'
            className='w-full rounded-sm border border-neutral-200 bg-neutral-50 px-2.5 py-2'
          >
            <Flex
              align='center'
              justify='between'
              gap='sm'
              className='mb-2 border-b border-neutral-200 pb-1.5'
            >
              <Flex align='center' gap='xs'>
                <MapPin
                  aria-hidden='true'
                  className='h-3.5 w-3.5 text-brand-500'
                />
                <Typography
                  as='span'
                  variant='body-xs'
                  weight='semibold'
                  className='text-neutral-800'
                >
                  Location details
                </Typography>
              </Flex>
              {lookup.externalUrl && (
                <a
                  href={lookup.externalUrl}
                  target='_blank'
                  rel='noreferrer'
                  className='inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-brand-500 hover:text-brand-700 hover:underline'
                >
                  View in Shiplify
                  <ExternalLink aria-hidden='true' className='h-3 w-3' />
                </a>
              )}
            </Flex>
            <Grid
              align='baseline'
              gapX='md'
              gapY='xs'
              className='grid-cols-[max-content_1fr] text-left'
            >
              {locationTypeLabel && (
                <>
                  <Typography as='span' variant='body-xs' textColor='muted'>
                    Location type
                  </Typography>
                  <Typography
                    as='span'
                    variant='body-xs'
                    weight='medium'
                    className='text-neutral-800'
                  >
                    {locationTypeLabel}
                  </Typography>
                </>
              )}
              {lookup.dockAccess != null && (
                <>
                  <Typography as='span' variant='body-xs' textColor='muted'>
                    Dock access
                  </Typography>
                  <Typography
                    as='span'
                    variant='body-xs'
                    weight='medium'
                    className='text-neutral-800'
                  >
                    {lookup.dockAccess ? 'Yes' : 'No'}
                  </Typography>
                </>
              )}
              {lookup.forklift != null && (
                <>
                  <Typography as='span' variant='body-xs' textColor='muted'>
                    Forklift
                  </Typography>
                  <Typography
                    as='span'
                    variant='body-xs'
                    weight='medium'
                    className='text-neutral-800'
                  >
                    {lookup.forklift ? 'Yes' : 'No'}
                  </Typography>
                </>
              )}
            </Grid>
          </Flex>
        )}
    </Flex>
  );
}
